/**
 * Nav-derived task-ADOPTION fields for StoreDetail (#2066).
 *
 * These decide which task/visit the rep resumes, so they must ALWAYS come from
 * the CURRENT navigation — never from a persisted cold-load snapshot. Restoring
 * stale nav could resume the wrong task (the #1944/#1994/#1996 family of bugs),
 * so deriveNavFromSearchParams is the single source of truth used by both
 * fetchStoreData and the cold-load hydrate.
 */

export interface StoreNavFields {
  projectIdFromNav: number | null;
  distributorIdFromNav: number | null;
  /** setId for sets, routeId for calls. */
  taskIdFromNav: number | null;
  taskTypeFromNav: 'set' | 'call' | null;
  projectDescFromNav: string | null;
  clientNameFromNav: string | null;
  callLogIdFromNav: number | null;
  /** Visit localId (uuid) from a queue-only check-in (#1987 Phase 3). */
  visitLocalIdFromNav: string | null;
  /**
   * Team config for a SET handoff (#3629). The non-lead deliverable
   * suppression must not race the background pending-tasks fetch, so the
   * sender (SetDetail, which holds the set's roster) passes the verdict
   * along and the active task carries it from birth. null = sender didn't
   * say (older link, call task) — the required-by-default rule applies.
   */
  teamProjectFromNav: boolean | null;
  teamLeadFromNav: boolean | null;
}

/** Derive the nav adoption fields from the current URL search params. */
export function deriveNavFromSearchParams(sp: URLSearchParams): StoreNavFields {
  const projectIdParam = sp.get('projectId');
  const distributorIdParam = sp.get('distributorId');
  const setIdParam = sp.get('setId');
  const routeIdParam = sp.get('routeId');
  const taskTypeFromNav = sp.get('taskType') as 'set' | 'call' | null;
  const callLogIdParam = sp.get('callLogId');
  return {
    projectIdFromNav: projectIdParam ? parseInt(projectIdParam, 10) : null,
    distributorIdFromNav: distributorIdParam ? parseInt(distributorIdParam, 10) : null,
    // Use setId for sets, routeId for calls.
    taskIdFromNav:
      taskTypeFromNav === 'set'
        ? setIdParam
          ? parseInt(setIdParam, 10)
          : null
        : routeIdParam
          ? parseInt(routeIdParam, 10)
          : null,
    taskTypeFromNav,
    projectDescFromNav: sp.get('projectDesc') ? decodeURIComponent(sp.get('projectDesc')!) : null,
    clientNameFromNav: sp.get('clientName') ? decodeURIComponent(sp.get('clientName')!) : null,
    callLogIdFromNav: callLogIdParam ? parseInt(callLogIdParam, 10) : null,
    visitLocalIdFromNav: sp.get('visitId'),
    teamProjectFromNav: sp.get('setTeam') === null ? null : sp.get('setTeam') === '1',
    teamLeadFromNav: sp.get('setLead') === null ? null : sp.get('setLead') === '1',
  };
}
